<script lang="ts">
import "@cisco-u/avatar/cu-avatar.js";

import { defineComponent, markRaw } from "vue";
import { mapGetters, mapMutations } from "vuex";

import MarqueeCardDay0 from "@/layout/Header/MarqueeCard.vue";
import PageHeader from "@/layout/Header/PageHeader.vue";
import dateFormats from "@/utils/dateFormats";
import { canShowGenAIModal } from "@/utils/genAIConsent";

export default defineComponent({
  emits: ["close", "open", "update-query"],
  components: {
    MarqueeCardDay0,
    PageHeader,
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      showGoal: false,
      headerHeight: 0,
      marqueeHeight: 0,
      marqueeFixed: false,
      resizeObserver: null as ResizeObserver | null,
    };
  },
  computed: {
    ...mapGetters({
      userStatus: "user/status",
      announcementBanner: "commerce/announcementBanner",
    }),
    initials() {
      return this.userStatus?.userInitials ?? "";
    },
    userDisplayName() {
      return this.userStatus?.display_name ?? "";
    },
    showPageHeader() {
      return this.$route?.meta?.pageHeader === true;
    },
    hideMarquee() {
      return this.$route?.meta?.hideMarquee === true;
    },
    showProfileHeader() {
      return this.$route?.meta?.showProfileHeader === true;
    },
    isPathRoute() {
      return this.$route?.name === "Path";
    },
    getUser() {
      if (!this.userStatus) return false;

      this.start();
      return true;
    },
    isPastUtc(): boolean {
      if (this.announcementBanner?.endDate) {
        return dateFormats.isPastUtc(this.announcementBanner.endDate);
      }
      return true;
    },
    // Offset the marquee by the measured height of the fixed header so it
    // always clears banners (e.g. the order-placement ALERT) that change the
    // header height at runtime. Falls back to the static Tailwind classes
    // until the first measurement runs.
    marqueeOffsetStyle(): Record<string, string> {
      if (this.headerHeight <= 0) return {};
      return this.marqueeFixed
        ? { top: `${this.headerHeight}px` }
        : { paddingTop: `${this.headerHeight}px` };
    },
    // Reserve enough space in the in-flow #profile container for the fixed
    // header plus the fixed marquee, so a taller header (e.g. when the ALERT
    // banner shows) never lets the marquee bleed into the content below it.
    profileMinHeightStyle(): Record<string, string> {
      if (
        this.hideMarquee ||
        !this.marqueeFixed ||
        this.headerHeight <= 0 ||
        this.marqueeHeight <= 0
      ) {
        return {};
      }
      return { minHeight: `${this.headerHeight + this.marqueeHeight + 16}px` };
    },
  },

  watch: {
    announcementBanner() {
      this.$nextTick(this.measureHeader);
    },
    hideMarquee() {
      this.$nextTick(this.measureHeader);
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.measureHeader();
      const headerEl = (this.$refs.pageHeader as any)?.$el as
        | HTMLElement
        | undefined;
      if (headerEl && "ResizeObserver" in globalThis) {
        this.resizeObserver = markRaw(
          new ResizeObserver(() => this.measureHeader())
        );
        this.resizeObserver.observe(headerEl);
      }
      window.addEventListener("resize", this.measureHeader);
    });
  },

  beforeUnmount() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    window.removeEventListener("resize", this.measureHeader);
    document.documentElement.style.removeProperty(
      "--announcement-banner-height"
    );
  },

  methods: {
    ...mapMutations({
      setupToggleShowAnyway: "user/setupToggleShowAnyway",
    }),
    closeModal() {
      this.$emit("close");
      this.setupToggleShowAnyway(false);
    },
    openModal() {
      this.$emit("open");
      this.setupToggleShowAnyway(true);
    },
    start() {
      if (
        !this.isGenAIConsent() &&
        this.userStatus &&
        this.$route?.name == "ForYou" &&
        !this.userStatus.surveyCompleted
      ) {
        this.openModal();
      }
    },
    updateQuery(newValue: any) {
      this.$emit("update-query", newValue);
    },
    isGenAIConsent() {
      return this.userStatus && canShowGenAIModal(this.userStatus);
    },
    measureHeader() {
      const headerEl = (this.$refs.pageHeader as any)?.$el as
        | HTMLElement
        | undefined;
      const marqueeEl = this.$refs.marqueeWrapper as HTMLElement | undefined;
      if (!headerEl) return;
      this.headerHeight = Math.ceil(headerEl.getBoundingClientRect().height);
      // Expose the dynamic order-placement ALERT banner height so other
      // fixed-header pages (e.g. Explore) can offset their content by it.
      const banner = headerEl.querySelector(
        '[data-cy="announcement-banner"]'
      ) as HTMLElement | null;
      const bannerHeight = banner
        ? Math.ceil(banner.getBoundingClientRect().height)
        : 0;
      document.documentElement.style.setProperty(
        "--announcement-banner-height",
        `${bannerHeight}px`
      );
      if (marqueeEl) {
        this.marqueeFixed = getComputedStyle(marqueeEl).position === "fixed";
        this.marqueeHeight = Math.ceil(
          marqueeEl.getBoundingClientRect().height
        );
      }
    },
  },
});
</script>

<template>
  <div>
    <div
      id="profile"
      :style="profileMinHeightStyle"
      :class="[
        {
          'min-h-[42rem] lg:min-h-[46rem] xl:min-h-[30rem]':
            (showPageHeader && !hideMarquee) ||
            (!showPageHeader && !hideMarquee && !isPastUtc),
        },
        {
          'min-h-[45.625rem] lg:min-h-[49.625rem] xl:min-h-[33.625rem]':
            !hideMarquee && isPastUtc,
        },
      ]"
    >
      <page-header
        ref="pageHeader"
        :dark="true"
        :scrolled="true"
        :show-logo="true"
        instance="profile-header"
        :searchQuery="searchQuery"
        @update-query="updateQuery"
      />

      <div
        ref="marqueeWrapper"
        v-show="!hideMarquee"
        :style="marqueeOffsetStyle"
        :class="[
          'mx-auto flex w-full justify-center pt-52 text-white sm:pt-44 md:fixed md:top-[8.625rem] md:justify-between md:pt-0 lg:top-[12.625rem]',
          {
            'md:left-0 md:right-0 md:top-20 md:pb-0 lg:top-36': showPageHeader,
          },
        ]"
      >
        <div :class="['container mx-auto flex justify-center gap-4 md:px-4']">
          <Marquee-Card-Day0
            :status="userStatus"
            @openGoSurveyModal="openModal"
            v-show="getUser"
          />
        </div>
      </div>
    </div>

    <div
      v-if="showProfileHeader"
      :class="[
        'mx-4 md:mb-[5.625rem] flex flex-row pl-4 md:container md:mx-auto',
        {
          'mt-56': hideMarquee,
          'mt-12': !hideMarquee,
        },
      ]"
    >
      <div>
        <cu-avatar
          cisco_two="true"
          :letters="initials"
          width="6rem"
          height="8.0625rem"
          fontSize="2.8125rem"
          class="pointer-events-none"
        ></cu-avatar>
      </div>
      <div class="flex flex-col pl-11">
        <p class="text-xl md:text-3xl text-white">Welcome back,</p>
        <p class="text-3xl md:text-7xl leading-[1.3] text-white">
          {{ userDisplayName }}
        </p>
      </div>
    </div>
  </div>
</template>
